import { useState } from "react";
import { AlarmClock, Check, ChevronDown, Pencil, Trash2, User, Wrench } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { money } from "@/lib/format";

const ORANGE = "var(--glow-home)";

function dueText(t, today) {
  if (!t.due) return "Completed";
  const days = Math.round((new Date(t.due) - new Date(today)) / 86400000);
  if (days < 0) return `${-days} day${days === -1 ? "" : "s"} overdue`;
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  return `Due ${fmtDate(t.due)}`;
}

export function TaskRow({ t, today, canDelete, onDone, onSnooze, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const [snz, setSnz] = useState(false);
  const late = t.group === "overdue";
  return (
    <li className={`q-card q-rise relative p-4 ${snz ? "z-30" : ""}`} data-testid={`task-${t.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-xl" style={{ background: `rgba(${ORANGE},0.14)` }}>{t.icon}</span>
        <div className="min-w-0 flex-1 basis-[calc(100%-60px)] sm:basis-0">
          <p className="truncate font-semibold text-q-text" data-testid={`task-title-${t.id}`}>{t.title}</p>
          <p className="text-xs text-q-muted">
            <span style={{ color: late ? "rgb(var(--glow-media))" : undefined }} className={late ? "font-bold" : ""} data-testid={`task-due-${t.id}`}>{dueText(t, today)}</span>
            {" · "}{t.repeat_label} · {t.category}
            {t.assignee_name && <span className="ml-1 inline-flex items-center gap-1"><User className="h-3 w-3" />{t.assignee_name}</span>}
            {t.appliance_name && <span className="ml-1 inline-flex items-center gap-1"><Wrench className="h-3 w-3" />{t.appliance_name}</span>}
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
        {t.due && (
          <>
            <button className="q-btn q-btn-primary min-h-[44px]" onClick={onDone} data-testid={`task-done-btn-${t.id}`}><Check className="h-4 w-4" /> Done</button>
            <div className="relative">
              <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => setSnz(!snz)} data-testid={`task-snooze-btn-${t.id}`}><AlarmClock className="h-4 w-4" /> Snooze</button>
              {snz && (
                <div className="absolute left-0 z-20 mt-1 w-36 rounded-xl border border-q-line bg-q-surface p-1 shadow-xl sm:left-auto sm:right-0">
                  {[[1, "1 day"], [7, "1 week"]].map(([n, l]) => <button key={n} className="block min-h-[44px] w-full rounded-lg px-3 text-left text-sm text-q-text hover:bg-q-raised" onClick={() => { setSnz(false); onSnooze(n); }} data-testid={`task-snooze-${n}-${t.id}`}>{l}</button>)}
                </div>
              )}
            </div>
          </>
        )}
        <button className="q-btn q-btn-ghost min-h-[44px] min-w-[44px]" onClick={onEdit} aria-label="Edit task" data-testid={`task-edit-btn-${t.id}`}><Pencil className="h-4 w-4" /></button>
        {canDelete && <button className="q-btn q-btn-ghost min-h-[44px] min-w-[44px]" onClick={onDelete} aria-label="Delete task" data-testid={`task-delete-btn-${t.id}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      </div>
      {t.history.length > 0 && (
        <button className="mt-2 inline-flex min-h-[44px] items-center gap-1 text-xs font-semibold text-q-muted hover:text-q-text" onClick={() => setOpen(!open)} data-testid={`task-history-toggle-${t.id}`}>
          History ({t.history.length}) <ChevronDown className={`h-3 w-3 transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
        </button>
      )}
      {open && (
        <ul className="mt-1 space-y-1 border-l-2 pl-3 text-sm" style={{ borderColor: `rgba(${ORANGE},0.4)` }} data-testid={`task-history-${t.id}`}>
          {t.history.map((h) => <li key={h.id} className="text-q-muted"><span className="text-q-text">{fmtDate(h.date)}</span> · {h.by_name}{h.cost ? ` · ${money(h.cost)}` : ""}{h.note ? ` · ${h.note}` : ""}{h.spending_id ? " · added to spending" : ""}</li>)}
        </ul>
      )}
    </li>
  );
}

const LABELS = { overdue: "Overdue", soon: "Due soon", upcoming: "Upcoming", completed: "Completed" };

export function TaskGroup({ g, rows, ...rest }) {
  if (!rows.length) return null;
  return (
    <section className="mb-6" data-testid={`task-group-${g}`}>
      <h2 className="mb-2 text-xs font-black uppercase tracking-[0.18em]" style={{ color: g === "overdue" ? "rgb(var(--glow-media))" : `rgb(${ORANGE})` }}>{LABELS[g]} · {rows.length}</h2>
      <ul className="space-y-2">{rows.map((t) => <TaskRow key={t.id} t={t} {...rest.row(t)} today={rest.today} canDelete={rest.canDelete} />)}</ul>
    </section>
  );
}
